import type {
  CompteBoutique,
  CreerCompteInput,
  MajCompteInput,
  InviteMemberInput,
  PermissionEntry,
  PlatformUser,
  RoleWithPermissions,
  StoreMember,
  UpdateMemberInput,
} from '@iobiz/shared'
import { api } from '../../api/client.js'

/**
 * Utilisateurs et rôles.
 *
 * ⚠️ Contrairement au catalogue ou aux clients, ces écrans ne passent PAS par
 * la réplique locale et n'écrivent pas dans la file d'attente : gérer des
 * droits d'accès hors ligne n'aurait pas de sens — deux appareils déconnectés
 * pourraient se retirer mutuellement l'accès, et il faudrait arbitrer après
 * coup une situation qu'aucune règle de fusion ne sait trancher sans risque.
 */

export const fetchMembers = (storeId: string): Promise<{ data: StoreMember[] }> =>
  api.get<{ data: StoreMember[] }>(`/stores/${storeId}/members`)

export const inviteMember = (storeId: string, input: InviteMemberInput): Promise<StoreMember> =>
  api.post<StoreMember>(`/stores/${storeId}/members`, input)

export const updateMember = (
  storeId: string,
  memberId: string,
  input: UpdateMemberInput,
): Promise<StoreMember> =>
  api.patch<StoreMember>(`/stores/${storeId}/members/${memberId}`, input)

export const removeMember = (storeId: string, memberId: string): Promise<void> =>
  api.delete<void>(`/stores/${storeId}/members/${memberId}`)

export const fetchRoles = (
  storeId: string,
): Promise<{ roles: RoleWithPermissions[]; permissions: PermissionEntry[] }> =>
  api.get<{ roles: RoleWithPermissions[]; permissions: PermissionEntry[] }>(
    `/stores/${storeId}/roles`,
  )

export const saveRolePermissions = (
  storeId: string,
  roleId: string,
  permissionIds: string[],
): Promise<{ roles: RoleWithPermissions[] }> =>
  api.put<{ roles: RoleWithPermissions[] }>(`/stores/${storeId}/roles/${roleId}/permissions`, {
    permissionIds,
  })

// -------------------------------------------------------------- plateforme

export const fetchPlatformUsers = (): Promise<{ data: PlatformUser[] }> =>
  api.get<{ data: PlatformUser[] }>('/admin/users')

/**
 * Les comptes de l'ÉQUIPE, dans le schéma de la boutique.
 *
 * ⭐ Ils remplacent les appels que j'avais d'abord écrits sur la table commune.
 * Aucun garde-fou « ce compte travaille-t-il ailleurs ? » n'est nécessaire :
 * un compte d'équipe n'existe que dans son enseigne.
 */
export const fetchComptes = (storeId: string): Promise<{ data: CompteBoutique[] }> =>
  api.get<{ data: CompteBoutique[] }>(`/stores/${storeId}/staff`)

export const creerCompte = (storeId: string, input: CreerCompteInput): Promise<CompteBoutique> =>
  api.post<CompteBoutique>(`/stores/${storeId}/staff`, input)

export const majCompte = (
  storeId: string,
  compteId: string,
  input: MajCompteInput,
): Promise<CompteBoutique> =>
  api.patch<CompteBoutique>(`/stores/${storeId}/staff/${compteId}`, input)

export const supprimerCompte = (storeId: string, compteId: string): Promise<void> =>
  api.delete<void>(`/stores/${storeId}/staff/${compteId}`)

/**
 * Envoie à un compte d'équipe son lien de mot de passe.
 *
 * `url` n'est renseignée que si le courriel n'est PAS parti : elle vaut un
 * accès au compte, et n'a pas à s'afficher quand le message est arrivé.
 */
export const envoyerInvitationCompte = (
  storeId: string,
  compteId: string,
  motif: 'invitation' | 'reset',
): Promise<{ envoye: boolean; url: string | null }> =>
  api.post<{ envoye: boolean; url: string | null }>(
    `/stores/${storeId}/staff/${compteId}/invitation`,
    { motif },
  )
